/**
 * AgentOsDemoSalesHunter — V141.
 *
 * Public sandbox for "Agent Obchodník" — fractional growth lead that
 * finds market gaps, golden opportunities (cash cows), and funnel
 * ideas for a given niche/domain. LLM-backed with a deterministic
 * fallback skeleton if the model is unavailable.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Loader2, TrendingUp, Target, Sparkles, ListChecks } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const EXAMPLES = [
  { domain: "krasneboty.sk", niche: "premium dámska obuv",
    current_products: "Lodičky, čižmy, sandále, kabelky", monthly_revenue_eur: 18000 },
  { domain: "supermajstri.sk", niche: "B2B sprostredkovanie remeselníkov",
    current_products: "Záhradné práce, maliarstvo, elektroinštalácia", monthly_revenue_eur: 35000 },
  { domain: "ekosvet.cz", niche: "eco-friendly bytová chémia",
    current_products: "Aviváže, pracie prášky, čistiace prostriedky", monthly_revenue_eur: 12000 },
];

export default function AgentOsDemoSalesHunter() {
  const [form, setForm] = useState({
    domain: "", niche: "", current_products: "", monthly_revenue_eur: 0, lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });

  const loadExample = (i) => { setForm({ ...form, ...EXAMPLES[i] }); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.niche.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/sales-hunter`, form);
      setResult(r.data);
      setStatus({ loading: false, error: null });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "string" ? d : (d?.message || "request_failed") });
    }
  };

  const formPanel = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-neutral-950 border border-gray-800" data-testid="sales-hunter-form">
      <div className="flex flex-wrap gap-2 mb-2">
        {EXAMPLES.map((e, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`sales-hunter-example-${i}`}
            className="text-[11px] text-emerald-400 hover:text-emerald-300 border border-emerald-900 px-2 py-1">
            ↳ {e.domain}
          </button>
        ))}
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Doména shopu (voliteľné)</label>
        <input type="text" value={form.domain}
          onChange={(e) => setForm({ ...form, domain: e.target.value })}
          placeholder="napr. krasneboty.sk"
          data-testid="sales-hunter-domain"
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400" />
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Nika / vertikála *</label>
        <input type="text" required value={form.niche}
          onChange={(e) => setForm({ ...form, niche: e.target.value })}
          placeholder="napr. premium dámska obuv"
          data-testid="sales-hunter-niche"
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400" />
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Aktuálne produkty</label>
        <textarea rows={3} value={form.current_products}
          onChange={(e) => setForm({ ...form, current_products: e.target.value })}
          placeholder="Krátky zoznam kategórií / SKU"
          data-testid="sales-hunter-products"
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400" />
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Mesačný revenue (EUR, voliteľné)</label>
        <input type="number" min="0" value={form.monthly_revenue_eur}
          onChange={(e) => setForm({ ...form, monthly_revenue_eur: parseInt(e.target.value) || 0 })}
          data-testid="sales-hunter-revenue"
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400" />
      </div>
      <button type="submit" disabled={status.loading || !form.niche.trim()}
        data-testid="sales-hunter-submit"
        className="w-full bg-emerald-400 hover:bg-emerald-300 text-black font-black py-3 text-[12px] uppercase tracking-widest disabled:opacity-40">
        {status.loading ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <Target className="w-4 h-4 inline mr-2" />}
        {status.loading ? "Hľadám príležitosti…" : "Nájdi diery na trhu"}
      </button>
      {status.error && <p className="text-sm text-red-400" data-testid="sales-hunter-error">⚠️ {status.error}</p>}
    </form>
  );

  const outputPanel = (
    <div className="p-6 bg-neutral-950 border border-gray-800 space-y-4" data-testid="sales-hunter-output">
      {!result && !status.loading && (
        <div className="text-center text-gray-500 py-16">
          <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Vyplň niku a zisti, čo predáva konkurencia ale ty nie.</p>
        </div>
      )}
      {result && (
        <>
          {result.executive_summary && (
            <div className="border border-emerald-500/40 bg-emerald-950/20 p-3">
              <p className="text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1">Zhrnutie</p>
              <p className="text-xs text-gray-200">{result.executive_summary}</p>
            </div>
          )}
          {result.market_gaps?.length > 0 && (
            <div data-testid="result-market-gaps">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2"><TrendingUp className="w-3 h-3 inline mr-1" /> Diery na trhu</p>
              <ul className="space-y-2">
                {result.market_gaps.map((g, i) => (
                  <li key={i} className="border border-gray-900 p-2 text-xs">
                    <p className="font-bold text-white">{g.title}</p>
                    <p className="text-gray-400 mt-1">{g.rationale}</p>
                    <p className="text-[10px] text-emerald-400 mt-1">{g.effort_weeks}w · +{g.expected_uplift_pct}% uplift</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.golden_opportunities?.length > 0 && (
            <div data-testid="result-golden">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2">🐷 Zlaté prasiatka</p>
              <ul className="space-y-2">
                {result.golden_opportunities.map((g, i) => (
                  <li key={i} className="border border-amber-500/30 bg-amber-950/10 p-2 text-xs">
                    <p className="font-bold text-amber-200">{g.title}</p>
                    <p className="text-gray-300 mt-1">€{g.estimated_monthly_revenue_eur}/mes · marža {g.margin_pct}%</p>
                    <p className="text-gray-400 mt-1">{g.why}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.funnel_ideas?.length > 0 && (
            <div data-testid="result-funnels">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2">🪝 Funnely</p>
              <ul className="space-y-2">
                {result.funnel_ideas.map((f, i) => (
                  <li key={i} className="border border-gray-900 p-2 text-xs">
                    <p className="font-bold text-white">"{f.hook}"</p>
                    <p className="text-gray-400 mt-1">{f.step1} → {f.step2} → {f.step3} · {f.channel}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.improvements?.length > 0 && (
            <div data-testid="result-improvements">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2"><ListChecks className="w-3 h-3 inline mr-1" /> Vylepšenia</p>
              <ul className="space-y-1.5">
                {result.improvements.map((m, i) => (
                  <li key={i} className="text-xs text-gray-300"><span className="text-emerald-400 font-bold">{m.area}:</span> {m.fix}</li>
                ))}
              </ul>
            </div>
          )}
          {result.used_fallback && (
            <p className="text-[10px] text-gray-600 italic">ⓘ Použili sme rezervné odporúčania (LLM nedostupný).</p>
          )}
        </>
      )}
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-demo-sales-hunter"
      h1="Agent Obchodník"
      lead="Nájde diery na trhu, zlaté prasiatka, funnely a vylepšenia pre tvoju niku. Fractional CEO za €99/mes."
      left={formPanel}
      right={outputPanel}
    />
  );
}
